import {
  faCalendarAlt,
  faLocationDot,
  faPaperPlane
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useContext, useState } from 'react'
import z from 'zod'
import {
  type MeetingLocationData,
  MeetingLocationType,
  useNewMeetingMutation
} from '~/api/mutations/action'
import { useAgency } from '~/api/queries/agency'
import { useCompany } from '~/api/queries/company'
import { useOrder } from '~/api/queries/order'
import { useMeInfos } from '~/api/queries/user'
import {
  DateInput,
  Dropdown,
  Form,
  InputCol,
  InputLine,
  Submit,
  TextInput,
  TextareaInput
} from '~/components/Form'
import { AddressDropdown } from '~/components/Form/Input/AddressDropdown'
import { SubmitAlignment } from '~/components/Form/Submit'
import { ButtonVariation } from '~/components/Global/Interactions/Button'
import { ToastType } from '~/components/Global/Interactions/Toast/Toast'
import { AsideAction } from '~/components/Scopes/Actions/AsideAction'
import {
  locationAddress,
  locationAddressDetail,
  locationContainer
} from '~/components/Scopes/Actions/MeetingAction.css'
import type { AddressResponse } from '~/types/global'
import { toast } from '~/utils'
import { AsideActionContext } from '~/utils/contextes'
import { useCloseAction } from './hooks/useCloseAction'

const formSchema = z
  .object({
    subject: z.string().min(1, 'Le sujet est requis'),
    fromDatetime: z.date({
      coerce: true,
      errorMap: (issue, { defaultError }) => {
        if (issue.code === 'invalid_date') {
          return { message: 'Veuillez renseigner une date de début valide' }
        }
        return { message: defaultError }
      }
    }),
    locationType: z.nativeEnum(MeetingLocationType, {
      errorMap: () => {
        return { message: 'Veuillez renseigner un lieu' }
      }
    }),
    agencyId: z.coerce.number().optional(),
    contactId: z.coerce.number().optional(),
    address: z.string().optional(),
    toDatetime: z.date({
      coerce: true,
      errorMap: (issue, { defaultError }) => {
        if (issue.code === 'invalid_date') {
          return { message: 'Veuillez renseigner une date de fin valide' }
        }
        return { message: defaultError }
      }
    }),
    notes: z.string().optional()
  })
  .refine(
    (data) => data.locationType !== MeetingLocationType.AGENCY || data.agencyId,
    {
      message: "L'agence est requise",
      path: ['agencyId']
    }
  )
  .refine(
    (data) =>
      data.locationType !== MeetingLocationType.CONTACT || data.contactId,
    {
      message: 'Le contact est requis',
      path: ['contactId']
    }
  )
  .refine(
    (data) => data.locationType !== MeetingLocationType.CUSTOM || data.address,
    {
      message: "L'adresse est requise",
      path: ['address']
    }
  )
  .refine((data) => data.fromDatetime < data.toDatetime, {
    message: 'La date de début doit être antérieure à la date de fin',
    path: ['toDatetime']
  })

type FormValues = z.infer<typeof formSchema>

type MeetingActionProps = {
  applicantIds: number[]
  orderId?: number
}

export const MeetingAction = ({
  applicantIds,
  orderId
}: MeetingActionProps) => {
  const meetingCreationMutation = useNewMeetingMutation()
  const [locationData, setLocationData] = useState<MeetingLocationData | null>(
    null
  )
  const [contacts, setContacts] = useState<number[]>([])
  const closeAction = useCloseAction()
  const { setIsDirty } = useContext(AsideActionContext)
  const currentUser = useMeInfos()

  const { data: agencyData, isLoading: isAgencyLoading } = useAgency(
    locationData?.type === MeetingLocationType.AGENCY
      ? locationData.agencyId
      : currentUser?.agencies[0]?.id
  )
  const { data: orderData } = useOrder(orderId)
  const companyId = orderData?.company?.id
  const { data: companyData, isLoading: isCompanyLoading } =
    useCompany(companyId)

  const formatAddress = (address: AddressResponse | undefined) => {
    if (!address) return null

    const addressParts = []
    if (address.line1) addressParts.push(address.line1)
    if (address.line2) addressParts.push(address.line2)
    if (address.postcode || address.city) {
      addressParts.push(
        `${address.postcode || ''} ${address.city || ''}`.trim()
      )
    }
    if (address.country) addressParts.push(address.country)

    return addressParts.join(', ')
  }

  const onSubmit = async (values: FormValues) => {
    return meetingCreationMutation.mutateAsync(
      {
        subject: values.subject,
        fromDatetime: values.fromDatetime,
        toDatetime: values.toDatetime,
        notes: values.notes,
        location: locationData,
        applicants: applicantIds,
        orderId: orderId?.toString(),
        contacts
      },
      {
        onSuccess: () => {
          closeAction(true)
          toast(
            'Rendez-vous planifié',
            'Le rendez-vous a bien été planifié',
            ToastType.Success
          )
        },
        onError: (error) => {
          if (error.response?.code === 10) {
            toast(
              'Erreur lors de la planification du rendez-vous',
              'Le lieu renseigné implique un contact non participant',
              ToastType.Danger
            )
            return
          }
          toast(
            'Erreur lors de la planification du rendez-vous',
            'Une erreur est survenue lors de la planification du rendez-vous',
            ToastType.Danger
          )
        }
      }
    )
  }

  return (
    <AsideAction
      title="Planifier un rendez-vous"
      icon={faCalendarAlt}
      applicantIds={applicantIds}
    >
      <Form
        onSubmit={onSubmit}
        schema={formSchema}
        onDirtyStateChange={(isDirty) => {
          if (isDirty) {
            setIsDirty(true)
          }
        }}
      >
        <InputCol>
          <TextInput
            label="Sujet"
            name="subject"
            required
            autoFocus
            onChange={() => setIsDirty(true)}
          />

          <InputLine>
            <DateInput
              label="Début"
              name="fromDatetime"
              startName="fromDatetime"
              endName="toDatetime"
              placeholder="Choisir une date"
              required
              showTime={true}
              noPast={true}
              popperPlacement="bottom-end"
              onChange={() => setIsDirty(true)}
            />
            <DateInput
              label="Fin"
              name="toDatetime"
              startName="fromDatetime"
              endName="toDatetime"
              placeholder="Choisir une date"
              required
              showTime={true}
              noPast={true}
              popperPlacement="bottom-end"
              onChange={() => setIsDirty(true)}
            />
          </InputLine>

          <div className={locationContainer}>
            <Dropdown
              label="Lieu"
              name="locationType"
              options={[
                { label: 'A distance', value: MeetingLocationType.ONLINE },
                { label: 'Agence', value: MeetingLocationType.AGENCY },
                {
                  label: 'Contact',
                  value: MeetingLocationType.CONTACT
                },
                {
                  label: 'Adresse personnalisée',
                  value: MeetingLocationType.CUSTOM
                }
              ]}
              placeholder="Sélectionner un lieu"
              defaultValue={locationData?.type}
              onChange={(value) => {
                if (value === MeetingLocationType.AGENCY) {
                  setLocationData({
                    type: MeetingLocationType.AGENCY,
                    agencyId: currentUser?.agencies[0]?.id
                  })
                } else {
                  setLocationData({ type: value as MeetingLocationType })
                }
                setIsDirty(true)
              }}
            />

            {locationData?.type === MeetingLocationType.ONLINE && (
              <TextInput
                placeholder="Détails (lien de visioconférence, etc.)"
                standalone
                onChange={(e) => {
                  setLocationData({
                    type: MeetingLocationType.ONLINE,
                    details: (e.target as HTMLInputElement).value
                  })
                  setIsDirty(true)
                }}
              />
            )}

            {locationData?.type === MeetingLocationType.AGENCY && (
              <>
                <Dropdown
                  options={
                    currentUser?.agencies.map((agency) => ({
                      label: agency.name,
                      value: agency.id.toString()
                    })) ?? []
                  }
                  fullWidth
                  placeholder={
                    currentUser?.agencies.length &&
                    currentUser?.agencies.length > 1
                      ? 'Choisir une agence'
                      : 'Aucune agence associée à votre compte'
                  }
                  name="agencyId"
                  emptyMessage="Aucune agence disponible"
                  defaultValue={
                    locationData?.agencyId?.toString() ??
                    currentUser?.agencies[0]?.id.toString()
                  }
                  onChange={(value) => {
                    setLocationData({
                      type: MeetingLocationType.AGENCY,
                      agencyId: Number.parseInt(value)
                    })
                    setIsDirty(true)
                  }}
                />
                <div className={locationAddress}>
                  {isAgencyLoading ? (
                    <span className={locationAddressDetail}>
                      Chargement de l'agence...
                    </span>
                  ) : (
                    agencyData && (
                      <>
                        <FontAwesomeIcon icon={faLocationDot} />
                        <span>{agencyData.name}</span>
                        <span className={locationAddressDetail}>
                          {formatAddress(agencyData.address)}
                        </span>
                      </>
                    )
                  )}
                </div>
              </>
            )}

            {locationData?.type === MeetingLocationType.CONTACT && (
              <>
                <Dropdown
                  options={
                    companyData?.contacts
                      ?.filter((contact) => !contact.archived)
                      ?.map((contact) => ({
                        label: `${contact.firstname} ${contact.lastname}`,
                        value: contact.id.toString()
                      })) || []
                  }
                  name="contactId"
                  fullWidth
                  placeholder="Choisir un contact"
                  defaultValue={locationData?.contactId?.toString() ?? null}
                  onChange={(value) => {
                    setContacts([
                      ...contacts.filter((id) => id !== Number.parseInt(value)),
                      Number.parseInt(value)
                    ])
                    setLocationData({
                      type: MeetingLocationType.CONTACT,
                      contactId: Number.parseInt(value)
                    })
                    setIsDirty(true)
                  }}
                />
                {locationData.contactId && (
                  <div className={locationAddress}>
                    <FontAwesomeIcon icon={faLocationDot} />
                    <span className={locationAddressDetail}>
                      {formatAddress(
                        companyData?.contacts?.find(
                          (contact) => contact.id === locationData.contactId
                        )?.address ?? companyData?.address
                      )}
                    </span>
                  </div>
                )}
              </>
            )}
            {locationData?.type === MeetingLocationType.CUSTOM && (
              <AddressDropdown
                placeholder="Saisissez une adresse"
                name="address"
                onChange={(address) => {
                  setLocationData({
                    type: MeetingLocationType.CUSTOM,
                    address: address.formattedAddress
                  })
                  setIsDirty(true)
                }}
              />
            )}
          </div>

          <TextareaInput
            label="Notes"
            name="notes"
            onChange={() => setIsDirty(true)}
          />

          {orderId && (
            <Dropdown
              label="Contacts participants"
              onChange={(value) => {
                setContacts(value.map((id) => Number.parseInt(id)))
                setIsDirty(true)
              }}
              defaultValues={contacts.map((id) => id.toString())}
              optionsLoading={isCompanyLoading}
              options={
                companyData?.contacts
                  ?.filter((contact) => !contact.archived)
                  ?.map((contact) => ({
                    label: `${contact.firstname} ${contact.lastname}`,
                    value: contact.id.toString()
                  })) || []
              }
              placeholder="Sélectionner un ou plusieurs contacts"
              multiple={true}
            />
          )}

          <Submit
            buttonVariation={ButtonVariation.Primary}
            submitAlignment={SubmitAlignment.Right}
            loadingText="Planification..."
            successText="Planifié !"
            onSuccessReset={false}
          >
            <FontAwesomeIcon icon={faPaperPlane} />
            Planifier le rendez-vous
          </Submit>
        </InputCol>
      </Form>
    </AsideAction>
  )
}
